import { ReactNode, useMemo } from "react";

import { Header } from "components/elements";
import { useToggle } from "hooks/utils";
import { classes } from "utils/classes";

import styles from "./Accordion.scss";

export type AccordionHeaderType = "compact" | "default" | "section";

/**
 * When the accordion is collapsed, this is the height (in em) of the child component that remains visible. Each value
 * corresponds to a preview-N class in Accordion.scss.
 */
export type AccordionPreview =
  // eslint-disable-next-line no-magic-numbers
  0 | 2 | 4 | 6 | 8 | 10 | 12;

export type AccordionProps = {
  className?: string;
  disabled?: boolean;
  header: string | ((expanded: boolean) => ReactNode);
  headerType?: AccordionHeaderType;
  initialExpanded?: boolean;
  longContent?: boolean;
  preview?: AccordionPreview;
  dataTestId?: string;

  children: ReactNode;
};

export function Accordion({
  className = "",
  disabled = false,
  header,
  headerType = "default",
  initialExpanded = false,
  longContent = false,
  preview = 0,
  dataTestId,
  children,
}: AccordionProps) {
  const [expanded, toggleExpanded] = useToggle(initialExpanded);

  const headerStyle = useMemo<Partial<typeof Header.propTypes>>(() => {
    switch (headerType) {
      case "compact":
        return { color: "tertiary", type: "h6", uppercase: true };
      case "section":
        return {
          bold: true,
          color: "secondary",
          type: "h3",
          uppercase: true,
        };
      default:
        return { color: "primary", bold: true, type: "h4", uppercase: false };
    }
  }, [headerType]);

  return (
    <div
      className={classes(styles.container, className, {
        [styles.disabled]: disabled,
        [styles.expanded]: expanded,
      })}
      data-test-id={dataTestId}
    >
      <div
        className={classes(styles.headerContainer, styles[headerType])}
        onClick={!disabled ? toggleExpanded : undefined}
      >
        {typeof header === "function" ? (
          header(expanded)
        ) : (
          <Header className={styles.header} {...headerStyle}>
            {header}
          </Header>
        )}
        <div className={styles.icon} />
      </div>
      <div
        className={classes(styles.content, styles[`preview-${preview}`], {
          [styles.long]: longContent,
        })}
      >
        {children}
      </div>
    </div>
  );
}
